
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  GET_USER_DETAILS,
  USER_DETAILS_BY_ID,
  USER_PROFILE_BY_ID
} from "@/store/modules/auth/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import RecruiterAiCandidateCard from "@/components/recruiter/interview/ai/RecruiterAiCandidateCard.vue";
import RecruiterAiInterviewDetailsCard from "@/components/recruiter/interview/ai/RecruiterAiInterviewDetailsCard.vue";
import RecruiterCandidateInformationDialog from "@/components/recruiter/interview/ai/RecruiterCandidateInformationDialog.vue";
import {
  AI_INTERVIEW_INVITE_BY_ID,
  INIT_AI_INTERVIEW_INVITES,
  SCHEDULE_AI_INTERVIEW_INVITE
} from "@/store/modules/recruiter_ai_interviews/constants";
import {
  generate_final_report,
  get_latest_interview_report,
  upsert_interview_report
} from "@/store/apis/ml_interview";
import { send_interviewer_meeting_invite } from "@/store/apis/recruiter";
import { get_job_by_id } from "@/store/apis/common";

export default Vue.extend({
  name: "RecruiterPersonInterviewDetails",
  components: {
    RecruiterAiCandidateCard,
    RecruiterAiInterviewDetailsCard,
    RecruiterCandidateInformationDialog
  },
  data() {
    return {
      coverImage: require("@/assets/images/interviews/ai_interview_cover.png"),
      fallbackAvatar: require("@/assets/images/female-bot.png"),
      showCandidateInfo: false,
      candidateUser: null as any,
      candidateProfile: null as any,
      reportRecord: null as any,
      reportPollTimer: null as ReturnType<typeof setInterval> | null,
      generatingFinalReport: false,
      showRescheduleDialog: false,
      rescheduling: false,
      rescheduleForm: {
        scheduled_at: "",
        duration_minutes: 30
      }
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter_ai_interviews", {
      inviteById: AI_INTERVIEW_INVITE_BY_ID
    }),
    invite(this: any): any {
      const id = (this.$route.query.inviteId as string) || "";
      if (!id) return null;
      return (this as any).inviteById(id);
    },
    candidateName(this: any): string {
      if (this.candidateProfile?.profile?.personal_details?.name) {
        const raw = Object.values(this.candidateProfile.profile.personal_details.name)
          .filter((v: any) => v)
          .join(" ");
        return this.normalizeName(raw);
      }
      return this.normalizeName(this.invite?.candidate_name || "Interview Candidate");
    },
    candidateRole(this: any): string {
      const workHistory = this.candidateProfile?.profile?.work_history_detailed;
      if (workHistory && Object.values(workHistory).length > 0)
        return ((Object.values(workHistory)[0] as any).job_title ||
          "Candidate") as string;
      return this.invite?.candidate_role || "Candidate";
    },
    inviteRole(this: any): string {
      return this.invite?.job_role || this.invite?.candidate_role || this.candidateRole || "Candidate";
    },
    durationDisplay(this: any): string {
      const duration = Number(this.invite?.duration_minutes);
      return Number.isFinite(duration) && duration > 0
        ? `${duration} Minutes`
        : "30 Minutes";
    },
    avatarUri(this: any): string {
      return (
        this.candidateUser?.avatar_uri ||
        this.invite?.avatar_uri ||
        this.user?.avatar_uri ||
        this.fallbackAvatar
      );
    },
    reportReady(this: any): boolean {
      const audit = this.parsedAuditReport;
      return !!(
        this.reportRecord &&
        ((audit && Object.keys(audit).length > 0) ||
          this.reportRecord?.status === "completed")
      );
    },
    parsedAuditReport(this: any): Record<string, any> | null {
      const raw = this.reportRecord?.audit_report;
      if (!raw) return null;
      if (typeof raw === "object") return raw;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === "object" ? parsed : null;
        } catch (_e) {
          return null;
        }
      }
      return null;
    },
    reportStatusText(this: any): string {
      if (
        this.reportRecord &&
        Array.isArray(this.reportRecord?.human_transcript) &&
        this.reportRecord.human_transcript.length > 0 &&
        !this.reportReady
      ) {
        return "Transcript received. Generating audit report...";
      }
      return this.reportReady
        ? "Person interview result is ready."
        : "Person interview is not completed yet.";
    },
    parsedFinalReport(this: any): Record<string, any> | null {
      const raw = this.reportRecord?.final_report;
      if (!raw) return null;
      if (typeof raw === "object") return raw;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === "object" ? parsed : null;
        } catch (_e) {
          return null;
        }
      }
      return null;
    },
    finalReportReady(this: any): boolean {
      const final = this.parsedFinalReport;
      return !!(final && Object.keys(final).length > 0);
    },
    canGenerateFinalReport(this: any): boolean {
      return !this.generatingFinalReport && this.reportReady;
    },
    canReschedule(this: any): boolean {
      if (!this.invite) return false;
      const status = String(this.invite?.status || "").toLowerCase();
      if (status !== "scheduled") return false;
      const scheduledAt = String(this.invite?.scheduled_at || "").trim();
      const ts = new Date(scheduledAt).getTime();
      if (!Number.isFinite(ts)) return false;
      return Date.now() > ts;
    }
  },
  async created(this: any) {
    this.initInvites();
    await this.loadCandidateData();
    this.startReportPolling();
  },
  beforeDestroy(this: any) {
    this.stopReportPolling();
  },
  methods: {
    ...mapActions("auth", {
      fetchUserById: USER_DETAILS_BY_ID,
      fetchProfileById: USER_PROFILE_BY_ID
    }),
    ...mapActions("recruiter_ai_interviews", {
      initInvites: INIT_AI_INTERVIEW_INVITES,
      scheduleInvite: SCHEDULE_AI_INTERVIEW_INVITE
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION
    }),
    notify(this: any, message: string) {
      this.set_root_notification(message);
    },
    normalizeName(this: any, name: string) {
      const cleaned = String(name || "").replace(/\s+/g, " ").trim();
      if (!cleaned) return "Candidate";
      const parts = cleaned.split(" ");
      const collapsed = parts.filter(
        (p, i) => i === 0 || p.toLowerCase() !== parts[i - 1].toLowerCase()
      );
      const half = Math.floor(collapsed.length / 2);
      if (
        collapsed.length % 2 === 0 &&
        half > 0 &&
        collapsed.slice(0, half).join(" ").toLowerCase() ===
          collapsed.slice(half).join(" ").toLowerCase()
      ) {
        return collapsed.slice(0, half).join(" ");
      }
      return collapsed.join(" ");
    },
    async onResumeClick(this: any) {
      if (!this.candidateUser?.resume_uri) {
        this.notify("Resume is not available for this candidate.");
        return;
      }
      window.open(this.candidateUser.resume_uri, "_blank");
    },
    onCandidateInfoClick(this: any) {
      this.showCandidateInfo = true;
    },
    openRescheduleDialog(this: any) {
      if (!this.invite) return;
      const d = new Date(String(this.invite?.scheduled_at || ""));
      this.rescheduleForm = {
        scheduled_at: Number.isFinite(d.getTime())
          ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
          : "",
        duration_minutes: Number(this.invite?.duration_minutes || 30) || 30
      };
      this.showRescheduleDialog = true;
    },
    async submitReschedule(this: any) {
      if (!this.invite || !this.rescheduleForm.scheduled_at || !this.rescheduleForm.duration_minutes) return;
      this.rescheduling = true;
      try {
        const candidateId = Number(this.invite?.candidate_id || 0);
        const jobId = Number(this.invite?.job_id || 0) || null;
        const refreshedResp = candidateId
          ? await get_latest_interview_report(candidateId, "person", jobId)
          : null;
        const refreshedReport = refreshedResp?.data?.data || this.reportRecord || {};
        await this.scheduleInvite({
          interview_type: "person",
          job_id: Number(this.invite?.job_id || 0) || null,
          job_role: this.invite?.job_role || null,
          candidate_id: Number(this.invite?.candidate_id || 0),
          upload_response_id: this.invite?.upload_response_id || null,
          candidate_name: this.invite?.candidate_name || "Candidate",
          candidate_role: this.invite?.candidate_role || "Candidate",
          avatar_uri: this.invite?.avatar_uri || null,
          interviewer_email: this.invite?.interviewer_email || null,
          ms_teams_link: this.invite?.ms_teams_link || null,
          ms_teams_meeting_id: this.invite?.ms_teams_meeting_id || null,
          ms_teams_join_meeting_id: this.invite?.ms_teams_join_meeting_id || null,
          ms_teams_meeting_passcode: this.invite?.ms_teams_meeting_passcode || null,
          platform_room_id: this.invite?.platform_room_id || null,
          duration_minutes: Number(this.rescheduleForm.duration_minutes),
          scheduled_at: new Date(this.rescheduleForm.scheduled_at).toISOString()
        });

        const interviewerEmail = String(this.invite?.interviewer_email || "").trim();
        const teamsLink = String(this.invite?.ms_teams_link || "").trim();
        if (interviewerEmail && teamsLink) {
          const questionBank = Array.isArray(refreshedReport?.question_bank)
            ? refreshedReport.question_bank
            : [];
          const interviewerBrief =
            refreshedReport?.interviewer_brief &&
            typeof refreshedReport.interviewer_brief === "object"
              ? refreshedReport.interviewer_brief
              : null;
          await send_interviewer_meeting_invite({
            interviewer_email: interviewerEmail,
            candidate_name: this.invite?.candidate_name || "Candidate",
            job_role: this.invite?.job_role || this.inviteRole || "Interview Role",
            scheduled_at: new Date(this.rescheduleForm.scheduled_at).toISOString(),
            ms_teams_link: teamsLink,
            ms_teams_join_meeting_id: this.invite?.ms_teams_join_meeting_id || null,
            ms_teams_meeting_passcode: this.invite?.ms_teams_meeting_passcode || null,
            interviewer_brief: interviewerBrief,
            question_bank: questionBank
          });
        }

        await this.initInvites();
        await this.loadCandidateData();
        this.showRescheduleDialog = false;
        this.notify("Interview rescheduled and interviewer invite sent.");
      } finally {
        this.rescheduling = false;
      }
    },
    async loadCandidateData(this: any) {
      const candidateId = this.invite?.candidate_id;
      if (!candidateId) return;
      const [user, profile] = await Promise.all([
        this.fetchUserById(candidateId),
        this.fetchProfileById(candidateId)
      ]);
      this.candidateUser = user;
      this.candidateProfile = profile;
      await this.loadLatestPersonReport();
    },
    async loadLatestPersonReport(this: any) {
      const candidateId = this.invite?.candidate_id;
      const jobId = Number(this.invite?.job_id || 0) || null;
      if (!candidateId) {
        this.reportRecord = null;
        return;
      }
      try {
        const resp = await get_latest_interview_report(
          Number(candidateId),
          "person",
          jobId
        );
        this.reportRecord = resp?.data?.data || null;
      } catch (e) {
        this.reportRecord = null;
      }
    },
    startReportPolling(this: any) {
      this.stopReportPolling();
      this.reportPollTimer = setInterval(async () => {
        if (this.reportReady) {
          this.stopReportPolling();
          return;
        }
        await this.loadLatestPersonReport();
      }, 4000);
    },
    stopReportPolling(this: any) {
      if (this.reportPollTimer) {
        clearInterval(this.reportPollTimer);
        this.reportPollTimer = null;
      }
    },
    goToInterviewReport(this: any) {
      if (!this.reportReady) {
        this.notify("Person interview result is not available yet.");
        return;
      }
      this.$router.push({
        name: "recruiter-person-interview-report",
        query: {
          inviteId: this.invite?.id ? String(this.invite.id) : "",
          candidateId: this.invite?.candidate_id
            ? String(this.invite.candidate_id)
            : "",
          jobId: this.invite?.job_id ? String(this.invite.job_id) : "",
          view: "person"
        }
      });
    },
    goToInterviewGuidelines(this: any) {
      const query: Record<string, string> = {};
      if (this.invite?.id) query.inviteId = String(this.invite.id);
      if (this.invite?.job_id) query.jobId = String(this.invite.job_id);
      this.$router.push({
        name: "recruiter-person-interview-guidelines",
        query
      });
    },
    async resolveFinalResumeText(this: any): Promise<string> {
      const profile = this.candidateProfile?.profile || {};
      return JSON.stringify(profile || {}) || "Candidate resume";
    },
    async resolveFinalJdText(this: any): Promise<string> {
      const inviteJobId = Number(this.invite?.job_id || 0);
      if (inviteJobId) {
        try {
          const resp = await get_job_by_id(inviteJobId);
          const payload = resp?.data?.data || resp?.data || {};
          const description = String(
            payload?.job_description || payload?.description || payload?.jd_text || ""
          ).trim();
          if (description) return description;
        } catch (_e) {
          // ignore and fallback
        }
      }
      return (
        String(this.invite?.job_description || "").trim() ||
        String(this.invite?.job_role || "").trim() ||
        "Interview role"
      );
    },
    async generateAndOpenFinalReport(this: any) {
      if (!this.reportReady) {
        this.notify("Person interview result is not available yet.");
        return;
      }
      if (this.finalReportReady) {
        this.$router.push({
          name: "recruiter-person-interview-report",
          query: {
            inviteId: this.invite?.id ? String(this.invite.id) : "",
            candidateId: this.invite?.candidate_id
              ? String(this.invite.candidate_id)
              : "",
            jobId: this.invite?.job_id ? String(this.invite.job_id) : "",
            view: "final"
          }
        });
        return;
      }
      const candidateId = Number(this.invite?.candidate_id || 0);
      const jobId = Number(this.invite?.job_id || 0) || null;
      if (!candidateId) return;
      this.generatingFinalReport = true;
      try {
        const [personResp, aiResp] = await Promise.all([
          get_latest_interview_report(candidateId, "person", jobId),
          get_latest_interview_report(candidateId, "ai", jobId)
        ]);
        const person = personResp?.data?.data || {};
        const ai = aiResp?.data?.data || {};
        const aiReport = person?.ai_report || ai?.ai_report || {};
        const auditReport = person?.audit_report || {};
        const aiTranscript = ai?.ai_transcript || [];
        const humanTranscript = person?.human_transcript || [];
        if (!Object.keys(aiReport || {}).length || !Object.keys(auditReport || {}).length) {
          this.notify("AI report or audit report is missing.");
          return;
        }
        const resumeText = await this.resolveFinalResumeText();
        const jdText = await this.resolveFinalJdText();
        const finalResp = await generate_final_report({
          resume_text: resumeText,
          jd_text: jdText,
          pre_interview_form: {},
          ai_report: aiReport,
          audit_report: auditReport,
          ai_transcript: Array.isArray(aiTranscript) ? aiTranscript : [],
          human_transcript: Array.isArray(humanTranscript) ? humanTranscript : []
        });
        const finalReport =
          finalResp?.data?.data?.final_report || finalResp?.data?.final_report || {};
        if (!Object.keys(finalReport).length) {
          this.notify("Final report generation returned empty response.");
          return;
        }
        await upsert_interview_report({
          candidate_id: candidateId,
          interview_type: "person",
          job_id: jobId,
          ai_report: aiReport,
          interviewer_brief: person?.interviewer_brief || {},
          human_transcript: humanTranscript,
          audit_report: auditReport,
          final_report: finalReport,
          status: "completed"
        });
        await this.loadLatestPersonReport();
        this.$router.push({
          name: "recruiter-person-interview-report",
          query: {
            inviteId: this.invite?.id ? String(this.invite.id) : "",
            candidateId: this.invite?.candidate_id
              ? String(this.invite.candidate_id)
              : "",
            jobId: this.invite?.job_id ? String(this.invite.job_id) : "",
            view: "final"
          }
        });
      } finally {
        this.generatingFinalReport = false;
      }
    }
  }
});
